import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A heavy Great War biplane bomber parked on the ground: a long slab
// fuselage between two full-span stacked wings, twin engine nacelles slung
// mid-gap each swinging its own named propeller, small bombs racked under the
// lower wing, twin-wheel undercarriage under each nacelle and a tail skid.
// The looping "propellers-spin" animation turns both propellers about their
// own hubs. Faces +Z, spans a 2x2 pad.

const KHAKI = "#7a6f4e";
const CANVAS = "#cbbd93";
const WOOD = "#7a4a30";
const GUNMETAL = "#4a4d52";
const TYRE = "#33342f";

const FUS_Y = 0.38; // fuselage centreline height
const NAC_X = 0.45; // nacelle offset from the midline
const NAC_Y = 0.52; // nacelle centreline height (mid wing gap)
const HUB_Z = 0.40; // propeller hubs, ahead of the nacelles

function box(
  w: number, h: number, d: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

function cyl(
  r: number, h: number, seg: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, h, seg), color, position, rotation };
}

// One engine nacelle slung between the wings, with its named two-blade prop.
function nacelle(side: 1 | -1): Part[] {
  const x = side * NAC_X;
  const name = side > 0 ? "propeller-r" : "propeller-l";
  return [
    // nacelle body + round radiator front
    box(0.14, 0.15, 0.42, KHAKI, [x, NAC_Y, 0.12]),
    cyl(0.08, 0.07, 10, GUNMETAL, [x, NAC_Y, 0.345], [Math.PI / 2, 0, 0]),
    // struts tying the nacelle to both wings
    box(0.03, 0.46, 0.03, WOOD, [x, 0.54, -0.02]),
    box(0.03, 0.46, 0.03, WOOD, [x, 0.54, 0.24]),
    // the named propeller: hub + blade bar
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.06, 8), color: GUNMETAL,
      position: [x, NAC_Y, HUB_Z], rotation: [Math.PI / 2, 0, 0], name },
    { geometry: new THREE.BoxGeometry(0.045, 0.52, 0.022), color: WOOD,
      position: [x, NAC_Y, HUB_Z + 0.01], rotation: [0, 0, side * 0.5], name },
  ];
}

// A small bomb slung under the lower wing: body + nose cone, pointing forward.
function bomb(x: number): Part[] {
  const y = 0.215;
  const z = 0.10;
  return [
    cyl(0.032, 0.13, 8, GUNMETAL, [x, y, z], [Math.PI / 2, 0, 0]),
    { geometry: new THREE.ConeGeometry(0.032, 0.06, 8), color: TYRE,
      position: [x, y, z + 0.095], rotation: [Math.PI / 2, 0, 0] },
  ];
}

function parts(): Part[] {
  const out: Part[] = [
    // long fuselage: main slab, rounded nose block, tapering rear boom
    box(0.20, 0.20, 1.10, KHAKI, [0, FUS_Y, 0.10]),
    box(0.16, 0.16, 0.20, KHAKI, [0, 0.37, 0.72]),
    box(0.14, 0.14, 0.65, KHAKI, [0, 0.36, -0.75]),

    // open cockpits: nose gunner ring and pilot's pit behind the wings
    box(0.11, 0.03, 0.12, "#22231f", [0, 0.485, 0.62]),
    box(0.13, 0.03, 0.15, "#22231f", [0, 0.485, 0.34]),

    // stacked wings: lower through the fuselage, full-span upper
    box(1.70, 0.035, 0.38, KHAKI, [0, 0.30, 0.10]),
    box(1.90, 0.035, 0.40, KHAKI, [0, 0.74, 0.12]),

    // outboard interplane struts and cabane struts over the fuselage
    box(0.03, 0.42, 0.03, WOOD, [0.78, 0.52, -0.02]),
    box(0.03, 0.42, 0.03, WOOD, [0.78, 0.52, 0.24]),
    box(0.03, 0.42, 0.03, WOOD, [-0.78, 0.52, -0.02]),
    box(0.03, 0.42, 0.03, WOOD, [-0.78, 0.52, 0.24]),
    box(0.03, 0.26, 0.03, WOOD, [0.14, 0.61, 0.0]),
    box(0.03, 0.26, 0.03, WOOD, [0.14, 0.61, 0.24]),
    box(0.03, 0.26, 0.03, WOOD, [-0.14, 0.61, 0.0]),
    box(0.03, 0.26, 0.03, WOOD, [-0.14, 0.61, 0.24]),

    // roundels on the upper wing tips
    cyl(0.11, 0.015, 14, CANVAS, [0.72, 0.765, 0.12]),
    cyl(0.06, 0.015, 12, WOOD, [0.72, 0.773, 0.12]),
    cyl(0.11, 0.015, 14, CANVAS, [-0.72, 0.765, 0.12]),
    cyl(0.06, 0.015, 12, WOOD, [-0.72, 0.773, 0.12]),

    // tail: wide stabiliser, fin and canvas rudder
    box(0.70, 0.03, 0.26, KHAKI, [0, 0.37, -1.02]),
    box(0.025, 0.20, 0.20, KHAKI, [0, 0.48, -1.00]),
    // rudder: thicker than the fin so no faces are coplanar
    box(0.04, 0.25, 0.10, CANVAS, [0, 0.47, -1.13]),

    // twin engine nacelles with their propellers
    ...nacelle(1),
    ...nacelle(-1),

    // undercarriage: a twin-wheel unit under each nacelle
    cyl(0.11, 0.06, 12, TYRE, [NAC_X + 0.07, 0.11, 0.20], [0, 0, Math.PI / 2]),
    cyl(0.11, 0.06, 12, TYRE, [NAC_X - 0.07, 0.11, 0.20], [0, 0, Math.PI / 2]),
    cyl(0.11, 0.06, 12, TYRE, [-NAC_X + 0.07, 0.11, 0.20], [0, 0, Math.PI / 2]),
    cyl(0.11, 0.06, 12, TYRE, [-NAC_X - 0.07, 0.11, 0.20], [0, 0, Math.PI / 2]),
    cyl(0.014, 0.22, 6, GUNMETAL, [NAC_X, 0.11, 0.20], [0, 0, Math.PI / 2]),
    cyl(0.014, 0.22, 6, GUNMETAL, [-NAC_X, 0.11, 0.20], [0, 0, Math.PI / 2]),
    // gear legs from the lower wing down to each axle
    box(0.03, 0.20, 0.03, WOOD, [NAC_X, 0.195, 0.15], [0.25, 0, 0]),
    box(0.03, 0.20, 0.03, WOOD, [NAC_X, 0.195, 0.25], [-0.25, 0, 0]),
    box(0.03, 0.20, 0.03, WOOD, [-NAC_X, 0.195, 0.15], [0.25, 0, 0]),
    box(0.03, 0.20, 0.03, WOOD, [-NAC_X, 0.195, 0.25], [-0.25, 0, 0]),

    // tail skid holding the boom off the grass
    box(0.03, 0.28, 0.04, WOOD, [0, 0.15, -0.95], [0.35, 0, 0]),

    // bombs racked under the lower wing, inboard and outboard of the nacelles
    ...bomb(0.24),
    ...bomb(-0.24),
    ...bomb(0.64),
    ...bomb(-0.64),
  ];
  return out;
}

export const biplaneBomber = defineModel({
  id: "biplane-bomber",
  name: "Biplane Bomber",
  tags: ["vehicle", "wwi", "military", "aircraft", "biplane", "bomber", "animated", "low-poly"],
  batchId: "wwi",
  summary: "A twin-engine Great War bomber with spinning propellers and wing bombs.",
  description:
    "A heavy Great War biplane bomber parked on the ground: a long khaki " +
    "fuselage between full-span stacked wings, two engine nacelles slung " +
    "mid-gap each with its own named propeller, small bombs racked under the " +
    "lower wing, twin-wheel undercarriage under each nacelle and a tail " +
    "skid. The looping propellers-spin animation turns both propellers about " +
    "their hubs. Faces +Z, parks on a 2x2 pad.",
  author: "Fable 5",
  footprint: 1.0,
  tiles: { w: 2, h: 2 },
  parts,
  animations: [
    {
      id: "propellers-spin",
      label: "Propellers spinning",
      duration: 0.6,
      loop: true,
      tracks: [
        {
          target: "propeller-l",
          pivot: [-NAC_X, NAC_Y, HUB_Z],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.6, value: [0, 0, -Math.PI * 2] },
          ],
        },
        {
          target: "propeller-r",
          pivot: [NAC_X, NAC_Y, HUB_Z],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.6, value: [0, 0, Math.PI * 2] },
          ],
        },
      ],
    },
  ],
});
